import moment from 'moment';
import React, { FC, memo } from 'react';
import classnames from 'classnames';
import Svg from 'svg-inline-react';
import { replaceMessageStringVariables } from '@shared/utils/replaceMessageStringVariables';
import { FR_PR_BLACK } from '@shared/styles/vars';
import { Avatar } from '@src/components/Avatar';
import { HEX_COLOR_OPACITY } from '@src/util/getHexColorOpacity';
import FrankLogoSvg from '@src/svgs/frank-logo-new.svg';
import { getNameInitials } from '@src/util/getNameInitials';
import { MessageSenderEnum } from '@src/graphql/generated';
import { AvatarSize } from '@src/components/Avatar/enums/avatarSize.enum';
import { MessageBubble } from './MessageBubble';
import { MessageType } from './enums/messageType.enum';
import { MessageProps } from './props';
import './styles.scss';

const SENDER_TO_MESSAGE_TYPE: Record<MessageSenderEnum, MessageType> = {
  [MessageSenderEnum.BOT]: MessageType.BOT,
  [MessageSenderEnum.USER]: MessageType.USER,
  [MessageSenderEnum.RECRUITER]: MessageType.RECRUITER,
};

function getMessageStatus(isOutgoing: boolean, isReadByUser: boolean): string {
  if (isOutgoing) {
    return isReadByUser ? ' | Read' : ' | Delivered';
  }

  return '';
}

export const Message: FC<MessageProps> = memo(
  ({ message, isLastMessage, isSelected, onClick, avatarSize = AvatarSize.SMALL, user, openingTitle }) => {
    const sender = message?.sender;
    const text = replaceMessageStringVariables(message?.text || '', {
      name: user.name || '',
      title: openingTitle || '',
    });
    const recruiter = message?.recruiter;
    const createdAt = message?.sentAt;
    const isReadByUser = !!message?.readByUserAt;

    const isOutgoing = sender === MessageSenderEnum.RECRUITER;
    const isBot = sender === MessageSenderEnum.BOT;

    const avatarUrl = isOutgoing ? recruiter?.avatar?.fullUrl : user?.avatar?.fullUrl;
    const avatarColor = isOutgoing ? recruiter?.avatarColor : user.avatarColor;
    const nameInitials = isOutgoing ? getNameInitials(recruiter?.fullName) : getNameInitials(user.name);
    const messageType = sender ? SENDER_TO_MESSAGE_TYPE[sender] : undefined;

    if (!text) {
      return null;
    }

    return (
      <div
        className={classnames(
          'fr-message',
          `fr-message--${messageType}`,
          { 'fr-message--last': isLastMessage },
          { 'fr-message--selected': isSelected },
        )}
      >
        <div className="fr-message__container" onClick={onClick}>
          <div className={classnames('fr-message__wrapper', isOutgoing && 'fr-message__wrapper--outgoing')}>
            <Avatar
              avatarColor={isBot ? FR_PR_BLACK : avatarColor}
              className={classnames(
                'fr-message__avatar',
                !isLastMessage && 'fr-message__avatar--hide',
                messageType && `fr-message__avatar--${messageType}`,
              )}
              initials={nameInitials}
              opacity={isBot ? HEX_COLOR_OPACITY[0] : HEX_COLOR_OPACITY[10]}
              size={avatarSize}
              source={isBot ? undefined : avatarUrl}
            >
              {isBot && <Svg className="fr-message__bot-svg" src={FrankLogoSvg} />}
            </Avatar>

            <MessageBubble hasAccent={isOutgoing} isInvisible={isBot} text={text} />
          </div>

          {createdAt && (
            <div
              className={classnames(
                'fr-message__timestamp',
                isSelected && 'fr-message__timestamp--selected',
                isOutgoing && 'fr-message__timestamp--outgoing',
              )}
            >
              {moment(createdAt).format('DD MMM, HH:mm')} {getMessageStatus(isOutgoing, !!isReadByUser)}
            </div>
          )}
        </div>
      </div>
    );
  },
);

Message.displayName = 'Message';
